/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Table, type TableProps } from 'antd';
import { IOrderItem } from '@/interfaces/order';
import TableComponent from '@/components/table';
import { formatNumber } from '@/utils/dataUtils';
import { round, sumBy } from 'lodash';
import { mediaView } from '@/utils/mediaUtils';
import { twMerge } from 'tailwind-merge';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { DeleteFilled } from '@ant-design/icons';

interface IProps {
  orderItems: Partial<IOrderItem>[];
  isPacking?: boolean;
  onDelete: (rec: Partial<IOrderItem>) => void;
}

const PackingListItem = ({
  orderItems,
  isPacking = false,
  onDelete,
}: IProps) => {
  const columns: TableProps<Partial<IOrderItem>>['columns'] = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'productCode',
      width: 130,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'สินค้า',
      dataIndex: 'productName',
      className: 'font-bold',
    },
    {
      title: 'Lot Number',
      dataIndex: 'lotNumber',
      width: 120,
      className: 'font-bold',
      align: 'center',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'qtNo',
      width: 140,
      align: 'center',
      render: (_: number, rec) => (
        <div className="flex flex-row gap-3 cursor-pointer justify-center">
          <div
            className={twMerge(
              'flex items-center gap-1',
              !rec.qtNo && 'text-gray-400',
              rec.qtId && 'text-blue-600 underline',
            )}
            onClick={() => (rec.qtId ? mediaView({ id: rec.qtId }) : false)}
          >
            {_ ?? ''}
            {rec.qtId && (
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'COA',
      dataIndex: 'coa',
      key: 'coa',
      align: 'center',
      width: 220,
      render: (_, rec) => (
        <div
          className={twMerge(
            'flex gap-1 items-center',
            rec.coaId && 'text-blue-600 underline',
            'cursor-pointer',
          )}
          onClick={() => (rec.coaId ? mediaView({ id: rec.coaId }) : false)}
        >
          {_ ?? 'ไม่พบไฟล์ COA'}
          {rec.coaId && (
            <ArrowTopRightOnSquareIcon className="w-4 h-4 text-blue-600" />
          )}
        </div>
      ),
    },
    {
      title: 'จำนวน Package',
      width: 120,
      dataIndex: 'package',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(round(Number(rec?.quantity), 2), 2),
    },
    {
      title: 'หน่วย Package',
      width: 120,
      dataIndex: 'packageUnitName',
    },
    {
      title: 'จำนวน',
      width: 120,
      dataIndex: 'quantity',
      align: 'right',
      render: (_: number, rec) =>
        formatNumber(
          round(Number(rec?.quantity) * Number(rec?.conversion), 2),
          2,
        ),
    },
    {
      title: 'หน่วยขาย',
      width: 100,
      dataIndex: 'baseUnitName',
      className: 'font-bold',
    },
    {
      title: 'FOC',
      width: 60,
      dataIndex: 'isFoc',
      className: 'font-bold',
      align: 'center',
      render: (_: boolean) => (
        <div className="font-bold w-full cursor-pointer text-blue-500">
          {(_ && 'FOC') || ''}
        </div>
      ),
    },
    {
      title: 'ราคา (THB)',
      width: 120,
      dataIndex: 'unitPrice',
      align: 'right',
      className: 'font-bold text-green-700',
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(_, 2)}
        </div>
      ),
      hidden: isPacking,
    },
    {
      title: 'รวมเงิน (THB)',
      width: 120,
      dataIndex: 'totalPrice',
      align: 'right',
      fixed: 'right',
      className: 'font-bold',
      render: (_: number, rec) => (
        <div className={twMerge(rec.isFoc && 'line-through')}>
          {formatNumber(
            round(Number(rec.quantity) * Number(rec.unitPrice), 2),
            2,
          )}
        </div>
      ),
      hidden: isPacking,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      align: 'center',
      width: 40,
      className: 'font-bold',
      render: (_, rec) => (
        <DeleteFilled className="text-red-700" onClick={() => onDelete(rec)} />
      ),
    },
  ];
  const totalPackage = sumBy(orderItems || [], 'quantity');
  const totalQuantity = sumBy(orderItems || [], item =>
    round((item.quantity || 0) * (item.conversion || 0), 2),
  );

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<Partial<IOrderItem>>
        columns={columns}
        criteria={{}}
        data={{
          data: orderItems,
          totalRecords: 0,
        }}
        loading={false}
        onTableChange={() => {
          console.log();
        }}
        scrollLength={1600}
        showPagination={false}
        summary={() => {
          return (
            <Table.Summary fixed>
              <Table.Summary.Row className="bg-red-100">
                <Table.Summary.Cell
                  index={0}
                  colSpan={5}
                  className="font-bold items-end"
                  align="right"
                >
                  จำนวนทั้งหมด
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(totalPackage || 0, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                />
                <Table.Summary.Cell
                  index={0}
                  colSpan={1}
                  className="font-bold items-end"
                  align="right"
                >
                  {formatNumber(totalQuantity || 0, 2)}
                </Table.Summary.Cell>
                <Table.Summary.Cell
                  index={0}
                  colSpan={3}
                  className="font-bold items-end"
                  align="right"
                />
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
};

export default PackingListItem;
